/**
 * The one place a refused action becomes visible.
 *
 * Mounted once, at the app root, so every mutation in the product is covered
 * rather than the ones somebody remembered to write an onError for. See
 * lib/mutationErrors.ts for why this exists: a role-gated button that fails
 * silently is indistinguishable from a broken page.
 *
 * A BOX IN THE MIDDLE OF THE SCREEN THAT HAS TO BE ACCEPTED (2026-09-30). It
 * was a card in the bottom-right corner that did not block. Shane: *"the red
 * box needs to be as per what the standard was as a pop up in the screen that
 * you have to accept you have seen"*, and *"in the normal popup that warns its
 * audits and registers the accept and by who"*.
 *
 * So it is the build standard's Dialog, the backdrop does not close it, and
 * accepting it writes one audit row per refusal (POST
 * /api/notifications/refusals/ack) saying who saw what, the way acknowledging
 * a warning notification does. The X and Escape count as accepting: they are
 * deliberate, and a box that could be put away without leaving the row would
 * make the row mean nothing.
 *
 * It sits above every other layer, including ConfirmModal's, because a refusal
 * that arrives while a confirmation is still open has to be seen over it.
 * Several refusals at once share the one box.
 *
 * THE HEADING SAYS WHAT FAILED (2026-10-02). It was "That didn't happen" for
 * everything. Shane: *"that poop up banner is so bad as a customer it is not
 * helpfull... if it failed to load it needs to say failed to load! or failed
 * to save or failed to download"*. Each entry knows its action (see
 * lib/mutationErrors.ts), and the heading is theirs - "Failed to Save", or two
 * of them joined when two different things failed at once.
 *
 * And a request that never reached the platform says so in words: the box
 * showed "Request failed with status 500 · HTTP_ERROR" twice when the API was
 * down behind the dev proxy (measured 2026-10-02). That status code was true
 * and useless to the person reading it.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from './Dialog';
import {
  subscribeToMutationErrors,
  dismissMutationError,
  WRITE_ACTIONS,
  type MutationErrorEntry,
} from '../lib/mutationErrors';
import { ackRefusal } from '../lib/notificationsApi';

export function MutationErrorToast(): JSX.Element | null {
  const { t } = useTranslation('common');
  const [entries, setEntries] = useState<MutationErrorEntry[]>([]);

  useEffect(() => subscribeToMutationErrors(setEntries), []);

  if (entries.length === 0) return null;

  /* What the person reads for an entry - and so what the audit row records. */
  const sentenceFor = (e: MutationErrorEntry): string => {
    if (!e.unreachable) return e.friendly;
    const consequence =
      e.action === 'save'
        ? t('errorBox.consequence.saved')
        : WRITE_ACTIONS.has(e.action)
          ? t('errorBox.consequence.changed')
          : t('errorBox.consequence.loaded');
    return t('errorBox.unreachable', { consequence });
  };

  const titles = [...new Set(entries.map((e) => t(`errorBox.title.${e.action}`)))];

  const accept = (): void => {
    const screen = window.location.pathname;
    for (const e of entries) {
      /* The audit row is the record, and writing it must never keep the box
       * on screen or break anything else (Rule 13), so a failed write is let
       * go rather than retried here. */
      // No query string: it can carry what someone typed.
      const request = e.request?.split('?')[0]?.slice(0, 500) ?? null;
      ackRefusal({ code: e.code, request, message: sentenceFor(e), screen }).catch(() => undefined);
      dismissMutationError(e.id);
    }
  };

  return (
    <Dialog
      title={titles.join(' · ')}
      onClose={accept}
      closeOnScrim={false}
      zIndex={10000}
      width={480}
      testId="mutation-error-toast"
      footer={
        <button type="button" className="btn pri" onClick={accept} data-testid="mutation-error-accept">
          {t('errorBox.acknowledge')}
        </button>
      }
    >
      {/* Announced, because somebody using a screen reader gets no other
        * signal that the button they pressed did nothing. */}
      <div role="alert">
        {/* The sentence is for the person. The code and the request go in the
          * audit row on Acknowledge, never on the box. */}
        {entries.map((e) => (
          <div key={e.id} className="alrt r" data-testid="mutation-error-entry">
            <div>
              {/* Two different things in the one box: each says which it is. */}
              {titles.length > 1 && (
                <div style={{ fontWeight: 600 }} data-testid="mutation-error-entry-title">
                  {t(`errorBox.title.${e.action}`)}
                </div>
              )}
              {/* A message may list several things, one per line (a part saved and
                * two of its suppliers not): keep the lines. */}
              <div style={{ whiteSpace: 'pre-line' }}>{sentenceFor(e)}</div>
              {e.count > 1 && (
                <div
                  style={{ marginTop: 4, fontSize: 10.5, color: 'var(--muted)', fontFamily: 'var(--ff)' }}
                  data-testid="mutation-error-count"
                >
                  {t('errorBox.times', { count: e.count })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Dialog>
  );
}
